import {Client, FeatureNames, Preferences, Profile, UserConfig} from "../../../types/UserConfig";
import {Optional} from "../../../js/util/Optional";
import {Branding} from "../../../types/Branding";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {BrokerKey} from "../../../types/BrokerKey";
import {BackOffice} from "../../../types/BackOffice";
import {StripeUserSubscriptionRecord} from "../../../stripe/models/StripeUserSubscriptionRecord";

export class CurrentUser {
    private constructor(private config: UserConfig) {
    }

    public get id(): number {
        return this.config.id;
    }

    public get username(): string {
        return this.config.username;
    }

    public hasWatchedTmcVideo() {
        // TODO: To enable feature whether user have watched tmc or not please enable this. currently set to true as default
        // Only Client Role needs to view the video, and only once
        // return this.config.client == null || this.config.client.watchedTmcVideo;

        return true;
    }

    public hasPassedMarginQuiz(): boolean {
        return Optional.of(this.config)
                       .map(uc => uc.client)
                       .map(c => c.passedOptionsQuiz)
                       .orElse(false);
    }

    public isMfaEnabled(): boolean {
        return this.config.isMfaEnabled;
    }

    public hasSwiftApplication(): boolean {
        return this.config.hasSwiftApplication;
    }

    public bannerMessage(): string {
        return this.config.bannerMessage;
    }

    public hasFeature(feature: FeatureNames): boolean {
        return this.config.features[feature] === true;
    }

    public hasAnyFeature(...features: ReadonlyArray<FeatureNames>): boolean {
        return features.some((f) => this.config.features[f] === true);
    }

    public hasAllFeatures(...features: ReadonlyArray<FeatureNames>): boolean {
        return features.every((f) => this.config.features[f] === true);
    }

    public get features(): ReadonlyArray<FeatureNames> {
        return ObjectUtil.keys(this.config.features);
    }

    public isAdviser(): boolean {
        return this.config.adviser != null;
    }

    public isClient(): boolean {
        return this.config.client != null;
    }

    public isRiskManager(): boolean {
        return this.config.riskManager != null;
    }

    public isAdmin(): boolean {
        return this.config.support != null;
    }

    public isSuperUser(): boolean {
        return this.config.super != null;
    }

    public isSupport(): boolean {
        return this.config.support != null;
    }

    public isOma(): boolean {
        return this.config.branding === "OPEN_MARKETS" ||
         this.config.branding === "BELLMONT" ||
         this.config.branding === "LIQUIDITY" ||
         this.config.branding === "ALLFIN" ||
         this.config.branding === "FUTURE_ASSIST";
    }

    public isGame(): boolean {
        return this.config.branding === "GAME";
    }

    public isIv(): boolean {
        return this.config.branding === "IV";
    }

    public isOpenTrader(): boolean {
        return this.config.branding === "OPEN_TRADER";
    }

    public isTc(): boolean {
        return this.config.branding === "TC";
    }

    public get ordersEnabled(): boolean {
        return this.config.ordersEnabled;
    }

    public get viewOrdersEnabled(): boolean {
        return this.config.viewOrdersEnabled;
    }

    public get shortName(): string {
        return this.config.shortName;
    }

    public isBranding(branding: Branding): boolean {
        return this.config.branding === branding;
    }

    public isLivePrices(): boolean {
        return this.config.livePrices === true;
    }

    public branding(): Branding {
        return this.config.branding;
    }

    public get imageUrl(): string {
        return this.config.imageUrl;
    }

    public static from(config: UserConfig): CurrentUser {
        return new CurrentUser(config);
    }

    public isStripeCustomer(): boolean {
        return this.config.stripeCustomerId != null;
    }

    public get watchlist(): ReadonlyArray<string> {
        return this.config.watchlist;
    }

    public updateWatchlist(watchlist: ReadonlyArray<string>) {
        this.config.watchlist = watchlist;
    }

    public get preferences(): Preferences {
        return this.config.preferences;
    }

    public get broker(): BrokerKey {
        return this.config.broker;
    }

    public get backOffice(): BackOffice {
        return this.config.backOffice;
    }

    public get profile(): Profile {
        return this.config.profile;
    }

    public get isLoggedIn(): boolean {
        return this.config.loggedIn;
    }

    public get hasOrderAdviceAccess(): boolean {
        return this.config.orderAdviceAccess;
    }

    public get isLoggedOut(): boolean {
        return !this.isLoggedIn;
    }

    public get client(): Client | undefined {
        return this.config.client;
    }
    public get hasCard(): boolean {
        return this.config.hasCard;
    }
    public get subscriptions(): Array<StripeUserSubscriptionRecord> {
        return this.config.subscriptions;
    }
}
